// ============================================================================
// GENERAL CONSTANTS
// ============================================================================

export const PREFIX = {
  sc: "sc",
  imp: "imp",
  bmi: "bmi",
  sal: "sal",
};

export const SC_ID = {
  newScenarioId: "@newScenarioID",
  existingScenarioId: "@scenarioId",
};

export const TABLES = {
  SCENARIO: "cmcdr.cost_model_scenario",
  IMPACTS: "cmcdr.cost_model_impacts",
  BMI: "cmcdr.base_material_index_scenario",
  SAL: "cmcdr.scenario_supplier_allocation",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getFieldsForTable(entityMapper, tableName) {
  return entityMapper?.[tableName]?.fields || [];
}

function getColumn(entityMapper, tableName, field) {
  return entityMapper?.[tableName]?.byField[field] || field;
}

// ============================================================================
// TRANSACTION WRAPPER TEMPLATES
// ============================================================================

export const TransactionTemplates = {
  wrapper: `BEGIN
                DECLARE @newScenarioID INT;
                BEGIN TRY
                  BEGIN TRANSACTION;`,

  end: `END TRY
                BEGIN CATCH
                    IF @@TRANCOUNT > 0
                        ROLLBACK TRANSACTION;

                    DECLARE
                        @ErrMsg NVARCHAR(4000) = ERROR_MESSAGE(),
                        @ErrSeverity INT = ERROR_SEVERITY(),
                        @ErrState INT = ERROR_STATE();

                    RAISERROR(@ErrMsg, 16, 1);

                    THROW;
                END CATCH;
            END`,
};

// ============================================================================
// SCENARIO TEMPLATES
// ============================================================================

export const ScenarioTemplates = {
  insertNew: (entityMapper) => {
    const table = TABLES.SCENARIO;
    const fields = getFieldsForTable(entityMapper, table).filter(
      (f) => f !== "scenarioId",
    );
    const columns = fields
      .map((f) => getColumn(entityMapper, table, f))
      .join(", ");
    const params = fields.map((f) => `:${PREFIX.sc}_${f}`).join(", ");

    return `INSERT INTO ${table} (${columns}) VALUES (${params});
                    SET ${SC_ID.newScenarioId} = SCOPE_IDENTITY();
                    SELECT ${SC_ID.newScenarioId} AS scenarioId;`;
  },

  updateExisting: (entityMapper) => {
    const table = TABLES.SCENARIO;
    const fields = getFieldsForTable(entityMapper, table).filter(
      (f) => f !== "scenarioId",
    );
    const setClause = fields
      .map((f) => `  ${getColumn(entityMapper, table, f)} = :${PREFIX.sc}_${f}`)
      .join(",\n");
    return `DECLARE ${SC_ID.existingScenarioId} INT = :scenarioId;
                    UPDATE ${table} SET
                    ${setClause}
                    WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.cost_model_impacts SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.results_inputs SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};
                    UPDATE cmcdr.results_prices SET obsolete_flag = 'X' WHERE scenario_id = ${SC_ID.existingScenarioId};`;
  },
};

// ============================================================================
// IMPACT TEMPLATES
// ============================================================================

export const ImpactTemplates = {
  build(isNewScenario, impactsCount, entityMapper) {
    if (impactsCount === 0) return "";

    const table = TABLES.IMPACTS;
    const scenarioRef = isNewScenario
      ? SC_ID.newScenarioId
      : SC_ID.existingScenarioId;
    const fields = getFieldsForTable(entityMapper, table).filter(
      (f) => f !== "scenarioId",
    );
    const columns = fields
      .map((f) => getColumn(entityMapper, table, f))
      .join(", ");

    const values = Array(impactsCount)
      .fill(0)
      .map((_, i) => {
        const prefix = `${PREFIX.imp}_${i}`;
        return `(${scenarioRef}, ${fields.map((f) => `:${prefix}_${f}`).join(", ")})`;
      })
      .join(",\n    ");

    return `INSERT INTO ${table} (scenario_id, ${columns}) VALUES\n    ${values};`;
  },
};

// ============================================================================
// BMI TEMPLATES — uses OPENJSON to pass all rows as a single JSON parameter,
// avoiding the ~2100 SQL parameter limit for large BMI arrays.
// ============================================================================

export const BMITemplates = {
  build(isNewScenario, bmiCount, entityMapper) {
    if (bmiCount === 0) return "";

    const table = TABLES.BMI;
    const scenarioRef = isNewScenario
      ? SC_ID.newScenarioId
      : SC_ID.existingScenarioId;
    const fields = getFieldsForTable(entityMapper, table).filter(
      (f) => f !== "scenarioId",
    );

    const withClause = fields
      .map((f) => {
        const col = getColumn(entityMapper, table, f);
        const quotedCol = col === "index" ? "[index]" : col;
        const type = f === "bm" ? "INT" : "NVARCHAR(MAX)";
        return `            ${quotedCol} ${type} '$.${f}'`;
      })
      .join(",\n");

    const insertCols = fields
      .map((f) => {
        const col = getColumn(entityMapper, table, f);
        return col === "index" ? "[index]" : col;
      })
      .join(", ");

    const srcVals = fields
      .map((f) => {
        const col = getColumn(entityMapper, table, f);
        return `src.${col === "index" ? "[index]" : col}`;
      })
      .join(", ");

    return `DECLARE @bmisJson NVARCHAR(MAX) = :bmisJson;

                    ;MERGE ${table} AS target
                    USING (
                        SELECT ${scenarioRef} AS scenario_id, j.*
                        FROM OPENJSON(@bmisJson)
                        WITH (
${withClause}
                        ) AS j
                    ) AS src
                    ON (target.scenario_id = src.scenario_id AND target.cost_model_key = src.cost_model_key AND target.bm = src.bm)
                    WHEN MATCHED THEN UPDATE SET
                        [index] = src.[index],
                        overwrite_index = src.overwrite_index,
                        flag = src.flag,
                        modified_by = src.modified_by,
                        last_updated = src.last_updated,
                        start_date = src.start_date
                    WHEN NOT MATCHED BY TARGET THEN INSERT (scenario_id, ${insertCols})
                    VALUES (${scenarioRef}, ${srcVals});`;
  },
};

// ============================================================================
// SAL TEMPLATES — uses OPENJSON to pass all rows as a single JSON parameter,
// avoiding the ~2100 SQL parameter limit for large SAL arrays.
// ============================================================================

export const SALTemplates = {
  build(isNewScenario, salCount, entityMapper) {
    if (salCount === 0) return "";

    const table = TABLES.SAL;
    const scenarioRef = isNewScenario
      ? SC_ID.newScenarioId
      : SC_ID.existingScenarioId;
    const fields = getFieldsForTable(entityMapper, table).filter(
      (f) => f !== "scenarioId",
    );
    const columns = fields
      .map((f) => getColumn(entityMapper, table, f))
      .join(", ");

    const withClause = fields
      .map((f) => {
        const col = getColumn(entityMapper, table, f);
        return `            ${col} NVARCHAR(MAX) '$.${f}'`;
      })
      .join(",\n");

    const srcCols = fields
      .map((f) => `j.${getColumn(entityMapper, table, f)}`)
      .join(", ");

    let sql = `DECLARE @salJson NVARCHAR(MAX) = :salJson;\n`;

    if (!isNewScenario) {
      sql += `                    UPDATE ${table} SET obsolete_flag = 'X' WHERE scenario_id = ${scenarioRef};\n`;
    }

    sql += `
                    INSERT INTO ${table} (scenario_id, ${columns})
                    SELECT ${scenarioRef}, ${srcCols}
                    FROM OPENJSON(@salJson)
                    WITH (
${withClause}
                    ) AS j;`;

    return sql;
  },
};

export const bindScenarioParams = (bindings, data, entityMapper) => {
  console.log(bindings, data, entityMapper);
  getFieldsForTable(entityMapper, TABLES.SCENARIO)
    .filter((f) => f !== "scenarioId")
    .forEach((field) => {
      if (data[field] !== undefined) {
        bindings.set(`${PREFIX.sc}_${field}`, data[field]);
      } else if (field === "originalScenarioId") {
        // Always set originalScenarioId to null if not provided to match SQL expectations
        bindings.set(`${PREFIX.sc}_${field}`, null);
      }
    });
};

export const bindImpactsParams = (bindings, data, entityMapper) => {
  const fields = getFieldsForTable(entityMapper, TABLES.IMPACTS).filter(
    (f) => f !== "scenarioId",
  );
  data.forEach((record, i) => {
    fields.forEach((field) => {
      bindings.set(`${PREFIX.imp}_${i}_${field}`, record[field]);
    });
  });
};

export const bindBMIParams = (bindings, data) => {
  bindings.set("bmisJson", JSON.stringify(data));
};

export const bindSALParams = (bindings, data) => {
  bindings.set("salJson", JSON.stringify(data));
};

export const bindScenarioId = (bindings, data) =>
  bindings.set("scenarioId", data.scenarioId);
